import { useGetNotificationsDeliveries } from '@agos/api-client/notifications'
import { T, formatCount, formatDateTime, tt } from '@agos/shared'
import { DataTable, EmptyState, ReadFailedBlock, cn, type Column } from '@agos/ui'
import { Inbox } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  capNote,
  deliveriesOf,
  deliveryReason,
  outcomeLabel,
  outcomeTone,
  summariseDeliveries,
  summaryParts,
  type DeliveryRow,
} from './deliveries'

/**
 * *Čo naozaj odišlo* — §9.253, FB-413.
 *
 * The delivery log had 64 rows and no screen, so *did the customer get their SMS* had no answer
 * anywhere in the manager. It sits under the switches that decide what is sent, which is where
 * somebody is standing when they ask.
 *
 * **Read-only.** The same sweep found three writes in this module; *turn a channel off everywhere*
 * stops every customer notification of that kind and is banked in `OWNER-QUESTIONS.md` rather than
 * decided here.
 *
 * **It says what happened and why - §9.771, owner O131.** Sereď's log read *Zlyhalo* over texts whose switch was
 * off and *Nebolo komu* over an SMS that was not needed because the push had reached the customer (FB-1090), and the
 * reason the server stores was drawn nowhere (FB-1091). Each outcome now has its own label and tone, a *Prečo* column
 * says why, and a read that failed says so rather than *Zatiaľ žiadne pokusy*.
 */

const LIMIT = 100

const TONE_CLASSES = {
  sent: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  failed: 'bg-red-500/15 text-red-600 dark:text-red-400',
  // A switch somebody turned off - something to look at, not a fault (§9.771).
  disabled: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  // Neither good news nor bad: nobody to send to is a fact about the recipient, not the channel.
  nobody: 'bg-muted text-muted-foreground',
  // Not needed: an earlier channel reached the customer (§9.771).
  skipped: 'bg-muted text-muted-foreground',
  pending: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
} as const

export function DeliveryLog() {
  const [channel, setChannel] = useState('')
  const { data, isLoading, isError, refetch } = useGetNotificationsDeliveries({
    limit: LIMIT,
    ...(channel === '' ? {} : { channel }),
  })
  // §9.771 - nothing is drawn from a read that failed: not the rows, not the summary, not the cap note.
  const rows = useMemo(() => (isError ? [] : deliveriesOf(data)), [data, isError])
  const summary = useMemo(() => summariseDeliveries(rows), [rows])
  const cap = capNote(rows, LIMIT)

  const columns: Column<DeliveryRow>[] = [
    {
      key: 'createdAt',
      header: tt('Kedy'),
      render: (row) => (row.createdAt === null ? '-' : formatDateTime(row.createdAt)),
      sortable: true,
      sortValue: (row) => row.createdAt ?? '',
    },
    {
      key: 'eventKey',
      header: tt('Udalosť'),
      render: (row) => <span className="font-mono text-[11px]">{row.eventKey}</span>,
      sortable: true,
      sortValue: (row) => row.eventKey,
    },
    {
      key: 'channel',
      header: tt('Kanál'),
      render: (row) => row.channel,
      sortable: true,
      sortValue: (row) => row.channel,
    },
    {
      key: 'outcome',
      header: tt('Výsledok'),
      render: (row) => (
        <span
          className={cn(
            'text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full',
            TONE_CLASSES[outcomeTone(row.outcome)],
          )}
          data-delivery-outcome={row.outcome}
          data-delivery-tone={outcomeTone(row.outcome)}
        >
          {outcomeLabel(row.outcome)}
        </span>
      ),
      sortable: true,
      sortValue: (row) => row.outcome,
    },
    {
      key: 'reason',
      header: tt('Prečo'),
      render: (row) => {
        const reason = deliveryReason(row)
        if (reason.source === 'none') {
          return (
            <span className="text-muted-foreground" data-delivery-reason="none">
              -
            </span>
          )
        }
        return (
          <span
            data-delivery-reason={reason.source}
            className={cn(
              'text-[11px]',
              // A provider's own words are data, so they look like data.
              reason.source === 'provider' && 'font-mono',
              reason.source === 'unrecorded' && 'text-muted-foreground',
            )}
          >
            {reason.text}
          </span>
        )
      },
      sortable: true,
      sortValue: (row) => deliveryReason(row).text,
    },
    {
      key: 'recipient',
      header: tt('Komu'),
      // Null on every row of this data — a push with nobody subscribed to receive it.
      render: (row) => row.recipient ?? <span className="text-muted-foreground">-</span>,
    },
    {
      key: 'order',
      header: tt('Objednávka'),
      className: 'text-right tabular-nums',
      render: (row) => (row.orderId === null ? '-' : `#${row.orderId}`),
      sortable: true,
      sortValue: (row) => row.orderId ?? 0,
    },
  ]

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-delivery-log={rows.length}>
      <div className="px-5 py-4 border-b border-border space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <T>Čo naozaj odišlo</T>
            </h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <T>Každý pokus o odoslanie - kedy, ktorým kanálom a ako dopadol.</T>
            </p>
          </div>
          <select
            data-delivery-channel={channel}
            aria-label={tt('Kanál')}
            value={channel}
            onChange={(event) => setChannel(event.target.value)}
            className="px-3 py-1.5 rounded-xl border border-border bg-background text-xs"
          >
            <option value="">{tt('Všetky kanály')}</option>
            <option value="push">push</option>
            <option value="sms">sms</option>
            <option value="email">email</option>
          </select>
        </div>

        {rows.length > 0 && (
          <p className="text-[11px] text-muted-foreground" data-delivery-summary={summary.total}>
            {formatCount(summary.total)} <T>pokusov</T>
            {summaryParts(summary).map((part) => (
              <span key={part.group} data-delivery-summary-part={part.group}>
                {' · '}
                {formatCount(part.count)} {part.word}
              </span>
            ))}
          </p>
        )}
        {cap !== '' && (
          <p className="text-[11px] text-amber-500" data-delivery-cap>
            {cap}
          </p>
        )}
      </div>

      <div className="px-2 py-2">
        {isError ? (
          <ReadFailedBlock
            marker="deliveries"
            title={tt('Záznam pokusov sa nepodarilo načítať')}
            onRetry={() => void refetch()}
          >
            <T>Neznamená to, že sa nič neposielalo - len sme sa k záznamu nedostali.</T>
          </ReadFailedBlock>
        ) : isLoading ? null : rows.length === 0 ? (
          // Marked, so a failed read can be shown not to draw it (§9.771): this sentence claims nothing was sent.
          <div data-delivery-empty>
            <EmptyState
              icon={<Inbox className="w-8 h-8" />}
              title={tt('Zatiaľ žiadne pokusy')}
              description={tt('Keď systém pošle notifikáciu, objaví sa tu - aj keď sa neodošle.')}
            />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(row) => String(row.id)}
            rowAttributes={(row) => ({ 'data-delivery': String(row.id) })}
            emptyMessage={tt('Zatiaľ žiadne pokusy')}
            pageSize={25}
            compact
          />
        )}
      </div>
    </div>
  )
}
